import { jsx as _jsx } from "react/jsx-runtime";
/**
 * A card drawn by a script — never in the page. The script runs in an `<iframe>` sandboxed to
 * `allow-scripts` alone (no `allow-same-origin`, so its origin is opaque and it reaches no cookie,
 * no storage and no page; no `allow-forms`, `allow-popups` or `allow-top-navigation`), from a
 * `srcdoc` whose CSP allows no network at all. It holds no token: the host reads the card's routes
 * with the viewer's session and hands the frame the rows by message, again at every refresh.
 *
 * What the frame may say back is a closed bridge, read by `bridgeMessage` and nothing else:
 * - `ready` — send the data again;
 * - `resize` — a height, held inside the card;
 * - `read` — one of the card's own `sources` by name (or `main`), read by the host as the card is;
 * - `action` — one of the card's own `forms`, which the host opens as the form it is, prefilled,
 *   and confirms with the viewer before anything is sent.
 * A message from any other window, or of any other kind, is refused. The frame's origin is `null`,
 * so it is never what a message is judged by: the message must come from this card's own frame.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { cleanCardScript, cleanCardStyle } from '@erp-sslab/schema/card-markup';
/** Exactly this: scripts, and nothing that would let them out. */
export const SCRIPT_SANDBOX = 'allow-scripts';
/** No request of any kind leaves the frame: no fetch, no image but a data one, no font, no frame, no form. */
export const SCRIPT_CSP = "default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'; img-src data:; base-uri 'none'; form-action 'none'";
/** The theme's variables a script's page is given, read from the page at the moment the data is sent — light or dark. */
export const SCRIPT_THEME_VARS = [
    'background', 'foreground', 'card', 'card-foreground', 'muted', 'muted-foreground', 'border', 'ring', 'brand',
    'tone-green', 'tone-amber', 'tone-red', 'tone-blue', 'tone-violet', 'tone-neutral',
];
/**
 * The frame's own code, run before the card's script: it takes away what a CSP does not cover
 * (peer connections, workers, a new window), and gives the script `card` — `onData`, `read`,
 * `action`, `resize` — over the one channel it has, its parent. It listens to its parent alone.
 */
const BOOT = `(function () {
  'use strict';
  var P = window.parent, seq = 0, waiting = {}, listeners = [], last = null;
  ['RTCPeerConnection', 'webkitRTCPeerConnection', 'RTCDataChannel', 'RTCSessionDescription', 'RTCIceCandidate',
   'WebSocket', 'WebTransport', 'EventSource', 'XMLHttpRequest', 'fetch', 'Worker', 'SharedWorker', 'BroadcastChannel', 'open'].forEach(function (k) {
    try { Object.defineProperty(window, k, { value: undefined, configurable: false, writable: false }); } catch (e) { /* already gone */ }
  });
  try { Object.defineProperty(navigator, 'sendBeacon', { value: undefined, configurable: false, writable: false }); } catch (e) { /* none */ }
  function send(m) { P.postMessage(m, '*'); }
  function ask(m) { return new Promise(function (res, rej) { seq += 1; waiting[seq] = { res: res, rej: rej }; m.id = seq; send(m); }); }
  function theme(vars) {
    if (!vars) return;
    for (var k in vars) if (Object.prototype.hasOwnProperty.call(vars, k)) document.documentElement.style.setProperty('--' + k, String(vars[k]));
  }
  window.addEventListener('message', function (e) {
    if (e.source !== P) return;
    var m = e.data;
    if (!m || typeof m !== 'object') return;
    if (m.type === 'data') {
      last = m; theme(m.theme);
      document.documentElement.dir = m.dir === 'ltr' ? 'ltr' : 'rtl';
      for (var i = 0; i < listeners.length; i += 1) { try { listeners[i](m); } catch (err) { console.error(err); } }
    } else if (m.type === 'reply' && waiting[m.id]) {
      var w = waiting[m.id]; delete waiting[m.id];
      if (m.ok) w.res(m.data); else w.rej(new Error(m.error || 'refused'));
    }
  });
  window.card = Object.freeze({
    onData: function (fn) { if (typeof fn === 'function') { listeners.push(fn); if (last) fn(last); } },
    read: function (source) { return ask({ type: 'read', source: String(source) }); },
    action: function (target, values, row) { return ask({ type: 'action', target: String(target), values: values || {}, row: row == null ? null : String(row) }); },
    resize: function (h) { send({ type: 'resize', height: Number(h) }); }
  });
  var sized = 0;
  function fit() { var h = Math.ceil(document.documentElement.scrollHeight); if (h !== sized) { sized = h; send({ type: 'resize', height: h }); } }
  if (typeof ResizeObserver === 'function') new ResizeObserver(fit).observe(document.documentElement);
  window.addEventListener('load', fit);
  send({ type: 'ready' });
})();`;
/** The frame's whole document. The script was read by `cleanCardScript`, so it cannot close its own tag. */
export function scriptSrcdoc(script, css, dir, lang) {
    return [
        `<!doctype html><html lang="${lang === 'en' ? 'en' : 'ar'}" dir="${dir}"><head><meta charset="utf-8">`,
        `<meta http-equiv="Content-Security-Policy" content="${SCRIPT_CSP}">`,
        '<style>:root{color-scheme:light dark}html,body{margin:0;background:transparent;color:var(--foreground);font:13px/1.5 system-ui,-apple-system,"Segoe UI",sans-serif}*{box-sizing:border-box}',
        css,
        '</style>',
        `<script>${BOOT}</script>`,
        `</head><body><script>${script}</script></body></html>`,
    ].join('\n');
}
const isScalar = (v) => v === null || typeof v === 'boolean' || (typeof v === 'number' && Number.isFinite(v)) || (typeof v === 'string' && v.length <= 2000);
/**
 * One message, judged. `frame` is this card's own frame window: a message whose `source` is any
 * other window is refused before its content is looked at. A read names a source the card declares
 * (or `main`); an action names one of the card's forms, with values that are plain and few; the
 * row, when there is one, is found by the host among the card's own rows — never taken on trust.
 */
export function bridgeMessage(event, frame, card) {
    if (!frame || event.source !== frame)
        return { kind: 'refused', reason: 'frame' };
    const m = event.data;
    if (!m || typeof m !== 'object' || Array.isArray(m))
        return { kind: 'refused', reason: 'shape' };
    const d = m;
    const id = typeof d['id'] === 'number' && Number.isInteger(d['id']) && d['id'] > 0 ? d['id'] : undefined;
    switch (d['type']) {
        case 'ready': return { kind: 'ready' };
        case 'resize': return typeof d['height'] === 'number' && Number.isFinite(d['height']) ? { kind: 'resize', height: d['height'] } : { kind: 'refused', reason: 'shape' };
        case 'read': {
            if (id === undefined)
                return { kind: 'refused', reason: 'shape' };
            const source = d['source'];
            const known = typeof source === 'string' && (source === 'main' || Object.prototype.hasOwnProperty.call(card.sources ?? {}, source));
            return known ? { kind: 'read', id, source: source } : { kind: 'refused', reason: 'source', id };
        }
        case 'action': {
            if (id === undefined)
                return { kind: 'refused', reason: 'shape' };
            const target = d['target'];
            if (typeof target !== 'string' || !(card.forms ?? []).some((f) => f.action === target))
                return { kind: 'refused', reason: 'target', id };
            const values = d['values'] ?? {};
            if (!values || typeof values !== 'object' || Array.isArray(values))
                return { kind: 'refused', reason: 'values', id };
            const entries = Object.entries(values);
            if (entries.length > 20 || entries.some(([k, v]) => !/^[A-Za-z_][A-Za-z0-9_]{0,63}$/.test(k) || !isScalar(v)))
                return { kind: 'refused', reason: 'values', id };
            const row = d['row'];
            if (row !== null && row !== undefined && typeof row !== 'string')
                return { kind: 'refused', reason: 'values', id };
            return { kind: 'action', id, target, values: Object.fromEntries(entries), row: typeof row === 'string' ? row : null };
        }
        default: return { kind: 'refused', reason: 'kind', ...(id !== undefined ? { id } : {}) };
    }
}
/** A frame's height, held inside the card: never under a line, never over the body the card has. */
export function frameHeight(asked, max) {
    return Math.round(Math.min(Math.max(asked, 24), Math.max(24, max)));
}
/** The theme's variables as the page has them now, for the frame's page. */
export function themeNow() {
    if (typeof document === 'undefined' || typeof getComputedStyle !== 'function')
        return {};
    const style = getComputedStyle(document.documentElement);
    const out = {};
    for (const name of SCRIPT_THEME_VARS) {
        const v = style.getPropertyValue(`--${name}`).trim();
        if (v)
            out[name] = v;
    }
    return out;
}
/**
 * The frame. Its document is written once per script; the data is posted to it at every change.
 * A second `load` means the frame went somewhere its own document did not come from: it is taken
 * down and nothing is posted to it again.
 */
export function CardScriptFrame({ card, data, dir, lang, maxHeight, title, onRead, onAction }) {
    const frame = useRef(null);
    const [height, setHeight] = useState(Math.min(120, maxHeight));
    const [gone, setGone] = useState(false);
    /** The document whose first load was seen: a second load of it is the frame going somewhere else. */
    const loaded = useRef(null);
    const reading = useRef(new Set());
    const acting = useRef(false);
    const doc = useMemo(() => {
        const script = cleanCardScript(card.script ?? '');
        return script.stripped.length ? null : scriptSrcdoc(script.script, card.style ? cleanCardStyle(card.style).css : '', dir, lang);
    }, [card.script, card.style, dir, lang]);
    // A new document (the script or its style changed) is a new frame, drawn again even after the last was taken down.
    useEffect(() => { setGone(false); }, [doc]);
    const latest = useRef({ data, card, onRead, onAction, maxHeight });
    latest.current = { data, card, onRead, onAction, maxHeight };
    const post = (message) => {
        const w = frame.current?.contentWindow;
        if (!w || gone)
            return;
        // The frame's origin is opaque (`null`): '*' is the only target a message to it can name. It is
        // this frame's own window, and the frame cannot have gone anywhere else (a second load ends it).
        w.postMessage(message, '*');
    };
    const sendData = () => post({ type: 'data', ...latest.current.data, theme: themeNow(), dir });
    // Posted again only when what it holds changed, not at every render of the page around it.
    const dataKey = useMemo(() => JSON.stringify(data), [data]);
    useEffect(() => { sendData(); }, [dataKey]);
    useEffect(() => {
        const onMessage = (event) => {
            const w = frame.current?.contentWindow;
            const m = bridgeMessage(event, w, latest.current.card);
            if (m.kind === 'refused') {
                // Another window is not answered at all; this frame is told no.
                if (m.reason !== 'frame' && m.id !== undefined)
                    post({ type: 'reply', id: m.id, ok: false, error: m.reason });
                return;
            }
            if (m.kind === 'ready') {
                sendData();
                return;
            }
            if (m.kind === 'resize') {
                setHeight(frameHeight(m.height, latest.current.maxHeight));
                return;
            }
            if (m.kind === 'read') {
                if (m.source === 'main') {
                    post({ type: 'reply', id: m.id, ok: true, data: latest.current.data['rows'] ?? [] });
                    return;
                }
                const read = latest.current.onRead;
                if (!read || reading.current.has(m.source)) {
                    post({ type: 'reply', id: m.id, ok: false, error: read ? 'busy' : 'source' });
                    return;
                }
                reading.current.add(m.source);
                void read(m.source).then((r) => {
                    reading.current.delete(m.source);
                    post(r.ok ? { type: 'reply', id: m.id, ok: true, data: r.data } : { type: 'reply', id: m.id, ok: false, error: r.error });
                });
                return;
            }
            const act = latest.current.onAction;
            if (!act || acting.current) {
                post({ type: 'reply', id: m.id, ok: false, error: act ? 'busy' : 'target' });
                return;
            }
            acting.current = true;
            void act(m.target, m.values, m.row).then((sent) => {
                acting.current = false;
                post({ type: 'reply', id: m.id, ok: sent, ...(sent ? { data: null } : { error: 'not_sent' }) });
            });
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [gone, dir]);
    if (!doc || gone)
        return null;
    return (_jsx("iframe", { ref: frame, title: title, sandbox: SCRIPT_SANDBOX, srcDoc: doc, "data-home-script": "", referrerPolicy: "no-referrer", loading: "lazy", style: { width: '100%', height, border: 0, display: 'block', background: 'transparent', colorScheme: 'normal' }, onLoad: () => { if (loaded.current !== doc) {
            loaded.current = doc;
            return;
        } setGone(true); } }, doc));
}
//# sourceMappingURL=home-script.js.map